import { Recycle, HeartHandshake, Factory, ShieldCheck } from "lucide-react";
import { COMPANY, EA_BADGE_URL } from "@/lib/brand";

const POINTS = [
  {
    icon: Recycle,
    title: "Recycle & reuse first",
    body: "Metal, wood, cardboard, electricals and more are sorted and sent for recycling. We reuse as much as we possibly can, and landfill is always the last resort.",
  },
  {
    icon: HeartHandshake,
    title: "Donated to local charities",
    body: "Furniture and household items in good condition are passed on to local charities, so they can go on to help someone else in the community.",
  },
  {
    icon: Factory,
    title: "Legal disposal, every time",
    body: "Anything that can't be reused or recycled goes to licensed waste facilities. No fly-tipping and no shortcuts, so you never have to worry about where it ends up.",
  },
];

export default function EcoDisposalSection() {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-slate-100" data-testid="services-eco-section">
      <div className="max-w-5xl mx-auto px-6 sm:px-10">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">Eco-friendly disposal</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Cleared responsibly, with care for Peterborough.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Your things deserve better than a skip. We sort everything we collect and give as much of
            it as possible a second life, so your clearance is kinder to the planet and to our
            local community.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {POINTS.map(({ icon: Icon, title, body }, i) => (
            <div
              key={title}
              data-testid={`eco-point-${i}`}
              className="rounded-2xl border border-slate-200 bg-[#FAF8F5] p-6 hover:border-[#FFB870] hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <div className="h-12 w-12 rounded-xl bg-[#FFF3E6] border border-[#FFB870] flex items-center justify-center mb-4">
                <Icon className="h-6 w-6 text-[#FF7F00]" />
              </div>
              <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        <div
          data-testid="eco-licence-card"
          className="mt-8 rounded-3xl border border-[#FFB870] bg-gradient-to-br from-[#FFF7EE] to-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5"
        >
          <img
            src={EA_BADGE_URL}
            alt="Environment Agency Registered Waste Carrier"
            className="h-16 w-auto object-contain self-start sm:self-center shrink-0"
          />
          <div>
            <p className="flex items-start gap-2 font-display text-lg sm:text-xl font-bold text-slate-900">
              <ShieldCheck className="h-5 w-5 mt-1 shrink-0 text-[#FF7F00]" /> Registered Environment Agency Waste Carrier
            </p>
            <p className="mt-2 text-slate-600 leading-relaxed">
              We're fully registered to carry waste, so you can trust everything is handled properly
              and within the law. Licence No.{" "}
              <span className="font-semibold text-slate-900" data-testid="eco-licence-number">
                {COMPANY.wasteCarrierLicence}
              </span>
              .
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
